import type {
  CollectionMemberWithTravellerDTO,
  CollectionTicketClaimMemberStateDTO,
} from '@/api/trafikportalen';
import {
  collectionTypeToStudentType,
  type StudentCollectionType,
} from '@/helpers/collectionTypes';
import { isClaimErrorState } from '@/helpers/claimStates';
import type { StudentType } from '@/types/students';
import { collator } from '@/utils/collator';

export interface StudentClaimError {
  collectionMemberId: string;
  travellerId: string;
  firstName: string;
  lastName: string;
  nationalIdentityNumber?: string;
  active: boolean;
  inactivateAt?: string;
  studentType: StudentType;
  collectionType: StudentCollectionType;
  states: CollectionTicketClaimMemberStateDTO[];
}

export interface StudentClaimErrorGroup {
  collectionType: StudentCollectionType;
  members: CollectionMemberWithTravellerDTO[];
  states: CollectionTicketClaimMemberStateDTO[];
}

export const studentsWithClaimErrors = (
  groups: StudentClaimErrorGroup[],
): StudentClaimError[] => {
  const errors: StudentClaimError[] = [];

  for (const group of groups) {
    const statesByMember = new Map<
      string,
      CollectionTicketClaimMemberStateDTO[]
    >();

    for (const state of group.states) {
      if (!isClaimErrorState(state.state)) continue;
      const existing = statesByMember.get(state.collection_member_id) ?? [];
      existing.push(state);
      statesByMember.set(state.collection_member_id, existing);
    }

    for (const member of group.members) {
      const states = statesByMember.get(member.collection_member.id);
      if (!states?.length) continue;

      errors.push({
        collectionMemberId: member.collection_member.id,
        travellerId: member.traveller.id,
        firstName: member.traveller.first_name ?? '',
        lastName: member.traveller.last_name ?? '',
        nationalIdentityNumber: member.traveller.national_identity_number,
        active: member.collection_member.active,
        inactivateAt: member.collection_member.inactivate_at,
        studentType: collectionTypeToStudentType(group.collectionType),
        collectionType: group.collectionType,
        states,
      });
    }
  }

  return errors.sort((a, b) => {
    const byLastName = collator.compare(a.lastName, b.lastName);
    if (byLastName !== 0) return byLastName;
    return collator.compare(a.firstName, b.firstName);
  });
};
